import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from '@wix/da-ds/pkg/Flex';
import FacetRadioButton from '@wix/da-ds/pkg/FacetList/Facet/dsPresets/FacetRadioButton';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import { DiscountFormField } from '../../../types';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import { useController, useFormContext } from 'react-hook-form';

import s from './DiscountSelector.scss';

interface Props {}

const DiscountSelector: FC<Props> = () => {
  const { t } = useTranslation();
  const { watch, control, errors } = useFormContext();
  const appliesToCategory = watch(DiscountFormField.AppliesToCategory);
  const isTierDiscount = appliesToCategory === 'subscriptions';
  const error = errors[DiscountFormField.DiscountValue];

  const {
    field: { onChange: controllerOnChange, value, ref, ...remainingInputProps },
  } = useController({
    name: DiscountFormField.DiscountValue,
    control,
    rules: {
      required: true,
    },
  });

  const discountValueOptions = [
    { value: '10', label: '10%' },
    { value: '20', label: '20%' },
    { value: '30', label: '30%' },
    { value: '50', label: '50%' },
    { value: '70', label: '70%' },
  ];
  if (isTierDiscount) {
    discountValueOptions.push({ value: '100', label: 'Free' });
  }
  return (
    <FormElement
      inputId={DiscountFormField.DiscountValue}
      label={
        <InputLabelText isRequired>
          {t(
            `studio.discountModal.form.discountValueInput.${isTierDiscount ? 'firstMonth.' : ''}label`
          )}
        </InputLabelText>
      }
      noBottomSpacing
    >
      <Flex className={s['discount-value-input']} direction="row" gap={8}>
        {discountValueOptions.map(option => (
          <FacetRadioButton
            id={`${DiscountFormField.DiscountValue}-${option.value}`}
            key={option.value}
            size="medium"
            value={option.value}
            selected={option.value === value}
            onChange={e => {
              controllerOnChange(e);
              e.stopPropagation();
            }}
            {...remainingInputProps}
          >
            {option.label}
          </FacetRadioButton>
        ))}
      </Flex>
      <div className={s['error-message-container']}>
        {error && <InputErrorMessage>{error.message}</InputErrorMessage>}
      </div>
    </FormElement>
  );
};
DiscountSelector.displayName = 'DiscountSelector';

export default DiscountSelector;
